// Layer 3 (Transformations): the Metallurgist "Cross-area flow" table —
// map-role-dashboards.md §4a. Facts derive from the seed series at the
// session cursor; downstream impact prose omits non-reconciling ppt totals.

import type { SessionState } from '@/versions/v1/schema/scenario';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { dailySevenDaySeriesDelta, sevenDaySeriesDelta } from './LandingSeries.transform';

export const CROSS_AREA_COLLAPSED_ROW_COUNT = 3;

export type CrossAreaFlowRow = {
  id: string;
  area: string;
  variable: string;
  sevenDaysAgo: string;
  today: string;
  change: string;
  downstreamImpact: string;
  tone: 'bad' | 'warn' | 'ok';
};

function formatDelta(first: number, last: number, unit: string, decimals = 1): string {
  const delta = last - first;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${Math.abs(delta).toFixed(decimals)}${unit}`;
}

function readingValue(id: string, state: SessionState) {
  const projected = readingsAt(state).find((r) => r.id === id);
  const seed = readingByIdLookup(id);
  if (!seed) throw new Error(`Missing reading ${id}`);
  return projected ?? seed;
}

export function crossAreaFlowVisibleRows(
  state: SessionState,
  expanded: boolean,
  collapsed: boolean,
): CrossAreaFlowRow[] {
  const rows = crossAreaFlowRows(state);
  if (!collapsed || expanded) return rows;
  return rows.slice(0, CROSS_AREA_COLLAPSED_ROW_COUNT);
}

export function crossAreaFlowShowDisclosure(
  state: SessionState,
  expanded: boolean,
  collapsed: boolean,
): boolean {
  const rows = crossAreaFlowRows(state);
  return collapsed && !expanded && rows.length > CROSS_AREA_COLLAPSED_ROW_COUNT;
}

export function crossAreaFlowRows(state: SessionState): CrossAreaFlowRow[] {
  // The pH and P80 rows are read with `metProcessVarsChart`'s sampling — one
  // point per production day — so the `7 days ago` cell and the chart agree on
  // the same variable at the same point in time. P80 is the case that bites:
  // the chart plots daily (80 → 82) while the series' own shift cadence would
  // start the window on a 1 Jun night shift (78). The reagent dose row is not
  // charted and keeps its shift window, which is the window that shows the dose
  // rising. The LIMS rows below keep this table's own 7 production days: since
  // #755 the LIMS assay chart plots the register's 12 h composites, and the
  // locked comp draws both windows side by side.
  const p80 = dailySevenDaySeriesDelta('rs-sa01-p80', state);
  // The pH row sits under the same daily chart as P80 and must match it: the
  // hourly series read over the last 168 points starts inside 1 Jun and
  // rounds to 11.0, while the chart's daily window opens at 11.0 on 2 Jun
  // and ends at the same 10.2 — the chart is the reference for both.
  const ph = dailySevenDaySeriesDelta('rs-zn-ph', state);
  const cuso4 = sevenDaySeriesDelta('rs-reag-cuso4', state);
  // The three LIMS rows are read over this table's 7 production days (the
  // chart's 12 h composites sit beside the register, not this table); grades
  // print to one decimal and the hourly/shift windows do not move them.
  const pbInZn = dailySevenDaySeriesDelta('rs-spec-zn-pb', state);
  const pbGrade = dailySevenDaySeriesDelta('rs-pb-grade', state);
  const znGrade = dailySevenDaySeriesDelta('rs-zn-grade', state);
  const batchStrength = readingValue('rd-cuso4-batch-strength', state);
  const batchValue = batchStrength.value ?? 162;
  const batchDesign = batchStrength.design ?? 180;

  return [
    {
      id: 'caf-1',
      area: 'Grinding',
      variable: 'Cyclone P80 (µm)',
      sevenDaysAgo: p80.first.toFixed(0),
      today: p80.last.toFixed(0),
      change: formatDelta(p80.first, p80.last, ' µm', 0),
      downstreamImpact: 'Coarser grind — contributing to Zn and Pb recovery loss',
      tone: p80.delta > 2 ? 'bad' : 'warn',
    },
    {
      id: 'caf-2',
      // AT-150 is the Zn rougher feed pH transmitter (TAG_MAPPING `sp-zn`,
      // loop AIC5002), so its row sits in Zn flotation (polish.P26
      // adjudication 5); the retired `Pb Flotation` label was a seed slip.
      area: 'Zn Flotation',
      variable: 'pH AT-150',
      sevenDaysAgo: ph.first.toFixed(1),
      today: ph.last.toFixed(1),
      change: formatDelta(ph.first, ph.last, '', 1),
      downstreamImpact: 'Below 10.8 threshold depresses Cu activation — Zn recovery impact',
      tone: ph.delta < -0.3 ? 'bad' : 'warn',
    },
    {
      id: 'caf-3',
      area: 'Zn Reagents',
      variable: 'CuSO4 dose (g/t)',
      sevenDaysAgo: cuso4.first.toFixed(0),
      today: cuso4.last.toFixed(0),
      change: formatDelta(cuso4.first, cuso4.last, ' g/t', 0),
      downstreamImpact: `Batch strength ${batchValue} g/L vs ${batchDesign} g/L minimum — under-activation (dose rate ${cuso4.last.toFixed(0)} g/t vs ${readingValue('rd-reag-cuso4', state).design ?? 150} design)`,
      tone: batchValue < batchDesign ? 'warn' : 'ok',
    },
    {
      id: 'caf-4',
      area: 'Zn Flotation',
      variable: 'Pb in Zn con (%)',
      sevenDaysAgo: pbInZn.first.toFixed(1),
      today: pbInZn.last.toFixed(1),
      change: formatDelta(pbInZn.first, pbInZn.last, '%', 1),
      downstreamImpact: 'Trending toward 3.0% smelter penalty — SMBS review needed',
      tone: pbInZn.delta > 0.2 ? 'warn' : 'ok',
    },
    {
      id: 'caf-5',
      area: 'Final grades',
      variable: 'Pb grade (%)',
      sevenDaysAgo: pbGrade.first.toFixed(1),
      today: pbGrade.last.toFixed(1),
      change: formatDelta(pbGrade.first, pbGrade.last, '%', 1),
      downstreamImpact: 'Above 62% spec — no action required',
      tone: 'ok',
    },
    {
      id: 'caf-6',
      area: 'Final grades',
      variable: 'Zn grade (%)',
      sevenDaysAgo: znGrade.first.toFixed(1),
      today: znGrade.last.toFixed(1),
      change: formatDelta(znGrade.first, znGrade.last, '%', 1),
      downstreamImpact: 'Above 55% spec — monitor',
      tone: znGrade.delta < -0.3 ? 'warn' : 'ok',
    },
  ];
}
